import { hasGroupAccess } from '../roles/resolve-features';
import { BASIC_ACCESS } from '../roles/built-in-roles';
import { expandGuids } from '../registry';
import { FEATURE_GROUP_REGISTRY } from '../registry';
import type { ResolvedFeatures } from '../roles/role-registry';
import { MENU_ITEMS } from '../menu/menu-registry';

export type MenuItem = {
  link: string;
  title: string;
};

/**
 * General-purpose entry point for external consumers of FRBAC.
 *
 * Hosts presentational queries that derive navigation data from a user's
 * roles, hiding role resolution, FEATURE_GROUP_REGISTRY expansion, and
 * the deny/override precedence rules behind a small public surface.
 */
export class AccessService {
  private static baseline: Set<string> | undefined;

  private static ReadEnv(name: string): string | undefined {
    return (globalThis as typeof globalThis & { process?: { env?: Record<string, string | undefined> } }).process?.env?.[name];
  }

  /**
   * Resolves the absolute base URL for the Account app from the
   * ENVIRONMENT process variable. Defaults to the dev URL when the
   * variable is missing or unrecognized so misconfiguration is loud
   * rather than silently routing to production.
   */
  private static GetLinkValue(): string {
    const environment = AccessService.ReadEnv('ENVIRONMENT');
    if (environment === 'production') {
      return 'https://account.socialgoodsoftware.com';
    }
    if (environment === 'staging') {
      return 'https://account.socialgoodsoftware.io';
    }
    return 'https://sgs.com:3005';
  }

  /**
   * BASIC_ACCESS is the baseline every member holds, but it only reaches a custom role that
   * inherits it and the roles UI does not force that. Resolving it here keeps a member who was
   * given one narrow custom role from losing the links to their own profile and dashboard.
   */
  private static GetBaseline(): Set<string> {
    if (!AccessService.baseline) {
      AccessService.baseline = new Set(expandGuids(BASIC_ACCESS.allow ?? [], FEATURE_GROUP_REGISTRY));
    }
    return AccessService.baseline;
  }

  /**
   * The features a menu is drawn from: what the member's roles resolved to, plus the baseline
   * every member holds. Deny is untouched, so an explicit deny still wins over the baseline.
   */
  public static GetMenuFeatures(features: ResolvedFeatures): ResolvedFeatures {
    return {
      allow: new Set([...features.allow, ...AccessService.GetBaseline()]),
      deny: features.deny,
      overrides: features.overrides,
    };
  }

  /**
   * Whether one menu entry's feature group is visible to the holder of these features. The one
   * rule every navigation in the suite asks, so a rail and the switcher cannot disagree.
   */
  public static IsMenuGroupVisible(groupGuid: string, features: ResolvedFeatures): boolean {
    return hasGroupAccess(groupGuid, AccessService.GetMenuFeatures(features));
  }

  /**
   * Returns the subset of MENU_ITEMS the user can see, given an already
   * resolved ResolvedFeatures. Synchronous — no DB or role-resolution
   * work. Suitable for in-app consumers (e.g. Account) that already
   * hold a cached ResolvedFeatures from FeatureContext.
   *
   * @param absolute When true (default) link is an absolute URL pointing
   * to the Account app for the current environment. When false the link
   * is the relative path (e.g. `/roles`).
   *
   * Visibility rule: an entry is included if the user has access to any
   * descendant leaf feature of the entry's groupGuid (deny / override
   * precedence applied via hasAccess). Render order matches MENU_ITEMS.
   */
  public static GetMenuItemsByFeatures(features: ResolvedFeatures, absolute = true): MenuItem[] {
    const baseUrl = absolute ? AccessService.GetLinkValue() : '';
    const menuFeatures = AccessService.GetMenuFeatures(features);
    const visibleItems: MenuItem[] = [];

    for (const entry of MENU_ITEMS) {
      if (!hasGroupAccess(entry.groupGuid, menuFeatures)) continue;

      visibleItems.push({
        link: `${baseUrl}${entry.path}`,
        title: entry.title,
      });
    }

    return visibleItems;
  }
}
